// Spanish (es-ES, formal register) UI dictionary for the QR Phone caller flow and
// staff softphone. Key-to-key mirror of en.ts — en.ts owns the `Dictionary` shape
// and the section comments; keep both files in the same order when adding keys.
//
// Register rules (company style): formal "usted" throughout — "Introduzca" (never
// "Ingrese"), "correctamente" (never "exitosamente"), "Active" (never "Habilite").
import type { Dictionary } from './en'

export const es: Dictionary = {
  // BRAND
  brand: 'Roommatik QR Phone',

  // ---------- LANGUAGE SWITCH (ScanLanding, StaffDashboard) ----------
  languageSwitchLabel: 'Idioma',
  languageSpanish: 'Español',
  languageEnglish: 'English',

  // ---------- CALLER: LANDING + FORM (ScanLanding) ----------
  landingDefaultTitle: 'Videollamada',
  landingIntro: 'Un miembro de nuestro equipo le atenderá por videollamada en un momento.',
  startCall: 'Iniciar llamada',
  loading: 'Cargando…',

  // ---------- CALLER: VIRTUAL ASSISTANT (ScanLanding choice, StaffNotAvailable) ----------
  assistantChoiceIntro: '¿Cómo prefiere que le atendamos?',
  assistantTalk: 'Hable con nuestra asistente virtual',
  assistantCallReception: 'Llamar a recepción',
  assistantVoiceNote: 'Puede hablar con la asistente virtual o escribirle. Si habla, su navegador le pedirá acceso al micrófono.',
  assistantFallbackIntro: 'Mientras tanto, nuestra asistente virtual puede ayudarle ahora mismo.',
  assistantOpening: 'Abriendo la asistente…',
  assistantUnavailable: 'La asistente no está disponible en este momento. Puede llamar a recepción o dejarnos sus datos.',

  // CALLER: FORM VALIDATION (ScanLanding, StaffNotAvailable)
  fieldRequired: 'Introduzca este dato para continuar.',
  invalidEmail: 'Introduzca una dirección de correo electrónico válida.',
  invalidPhone: 'Introduzca un número de teléfono válido.',

  // CALLER: PERMISSIONS (ScanLanding)
  preparingCall: 'Preparando su llamada…',
  permissionsTitle: 'Permisos necesarios',
  permissionsIntro:
    'Para poder atenderle, necesitamos acceso a su micrófono y a su cámara. Su navegador le pedirá confirmación.',
  requestingPermissions: 'Solicitando permisos…',
  allowAndContinue: 'Permitir y continuar',
  back: 'Volver',
  permissionsDenied:
    'No hemos podido acceder a su cámara ni a su micrófono. Active los permisos en su navegador (el icono del candado junto a la dirección) e inténtelo de nuevo.',

  // CALLER: EXPIRED / INVALID / UNAVAILABLE (ScanLanding, InvalidQr)
  qrExpiredTitle: 'Este código ya no es válido',
  qrExpiredIntro:
    'El código que ha escaneado ha caducado o no es correcto. Inténtelo de nuevo más tarde o consulte a un miembro del personal del establecimiento.',
  linkExpiredTitle: 'Este enlace ha caducado',
  linkExpiredIntro: 'Los códigos de la pantalla solo sirven durante unos minutos. Vuelva a escanear el código que aparece en la pantalla.',
  linkUsedTitle: 'Este enlace ya se ha utilizado',
  linkUsedIntro: 'Cada código de la pantalla solo puede usarse una vez. Solicite un código nuevo en la pantalla y escanéelo.',
  invalidQrTitle: 'Código no válido',
  invalidQrIntro:
    'El código que ha escaneado no es válido. Escanee el código de nuevo o consulte a un miembro del personal del establecimiento.',

  // CALLER: SITE ROOT (RootLanding, roadmap #108)
  rootLandingTitle: 'Videollamada con recepción',
  rootLandingIntro:
    'Esta página se abre al escanear un código QR o pulsar el botón de llamada de un panel de Roommatik. Vuelva a intentarlo desde allí.',
  rootLandingStaffLink: 'Acceso del personal',

  serviceUnavailableTitle: 'Servicio no disponible',
  serviceUnavailablePrepare:
    'No hemos podido preparar la llamada. Compruebe su conexión a internet e inténtelo de nuevo en unos instantes.',
  serviceInactive: 'Este servicio no está activo en este momento. Inténtelo de nuevo más tarde.',
  notYetAvailableTitle: 'Aún no disponible',
  availableFrom: (when: string) => `Este servicio estará disponible a partir de ${when}.`,
  retry: 'Reintentar',

  // ---------- CALLER: IN-CALL / QUEUE (CallView, CallScreen) ----------
  checkConnection: 'Compruebe su conexión a internet.',
  connectingCall: 'Estamos conectando su llamada. Espere, por favor…',
  queuePosition: (n: number | string) => `Posición en la cola: ${n}`,
  cancel: 'Cancelar',
  beingAssisted: 'Le estamos atendiendo',
  cameraOff: 'Cámara apagada',
  unstableConnection: 'Conexión inestable. Intentando recuperar la llamada…',
  micOff: 'Micrófono silenciado',

  // CALLER: NO STAFF (CallView failure panel)
  cannotAssistTitle: 'No hemos podido atenderle',
  allBusy:
    'Todos los miembros de nuestro equipo están ocupados en este momento. Inténtelo de nuevo en unos minutos.',

  // ---------- CALLER: CALL ENDED (CallEnded) ----------
  callEndedTitle: 'Llamada finalizada',
  callDuration: (formatted: string) => `Duración: ${formatted}`,
  thanksForUsing: 'Gracias por utilizar este servicio.',
  needAnythingElse: '¿Necesita algo más?',
  callAgain: 'Llamar de nuevo',
  youMayCloseScreen: 'Ya puede cerrar esta pantalla.',

  // ---------- CALLER: CALL ERROR (CallError) ----------
  callErrorTitle: 'No hemos podido completar la llamada',
  callErrorIntro: 'Se ha producido un problema durante la llamada. Inténtelo de nuevo en unos minutos.',

  // ---------- CALLER: CALLBACK / NO STAFF (StaffNotAvailable) ----------
  callbackIntro:
    'Todos los miembros de nuestro equipo están ocupados en este momento. Si lo desea, déjenos un dato de contacto y le atenderemos lo antes posible.',
  callbackWantContact: '¿Desea que nos pongamos en contacto con usted?',
  yes: 'Sí',
  no: 'No',
  callbackDeclined: 'De acuerdo. Esperamos poder atenderle pronto. Ya puede cerrar esta pantalla.',
  callbackPreferredMethod: '¿Cómo prefiere que nos pongamos en contacto con usted?',
  emailLabel: 'Correo electrónico',
  phoneLabel: 'Número de teléfono',
  send: 'Enviar',
  callbackThanks:
    'Gracias. Hemos registrado sus datos y nos pondremos en contacto con usted lo antes posible.',
  // Shown instead of the contact form on public kiosks (no keyboard).
  callbackKioskMessage:
    'Todos los miembros de nuestro equipo están ocupados en este momento. Inténtelo de nuevo en unos instantes o consulte a un miembro del personal del establecimiento.',

  // ---------- CONTINUE THE CALLBACK ON THE GUEST'S OWN PHONE (#61) ----------
  callbackScanTitle: '¿Prefiere dejarnos sus datos?',
  callbackScanIntro: 'Escanee este código con su teléfono para dejarlos desde allí, usando su propio teclado.',
  callbackScanAlt: 'Código para continuar en su teléfono',
  callbackPhoneTitle: 'Deje sus datos de contacto',
  callbackPhoneIntro: 'Nadie ha podido atender su llamada. Deje sus datos y nos pondremos en contacto con usted.',
  callbackPhoneInvalidLink: 'Este enlace está incompleto. Escanee de nuevo el código de la pantalla.',

  // ---------- SHARED IN-CALL CONTROL BAR (CallControls aria-labels) ----------
  muteMic: 'Silenciar micrófono',
  unmuteMic: 'Activar micrófono',
  turnOffCamera: 'Apagar cámara',
  turnOnCamera: 'Encender cámara',
  switchCamera: 'Cambiar de cámara',
  hangUp: 'Colgar',

  // ---------- STAFF: LOGIN (StaffLogin) ----------
  staffLoginTitle: 'Acceso para el personal',
  staffLoginIntro: 'Introduzca sus datos para recibir llamadas en este dispositivo.',
  staffRedirecting: 'Redirigiendo al inicio de sesión…',
  passwordLabel: 'Contraseña',
  signingIn: 'Iniciando sesión…',
  signIn: 'Iniciar sesión',
  loginFailedGeneric: 'No hemos podido iniciar su sesión. Inténtelo de nuevo.',
  loginFailedCheckDetails: 'No hemos podido iniciar su sesión. Compruebe sus datos e inténtelo de nuevo.',
  loginFailedService: 'No hemos podido conectar con el servicio. Inténtelo de nuevo.',
  // STAFF: SIGNED IN ELSEWHERE (single-device prompt)
  otherDeviceTitle: 'Sesión iniciada en otro dispositivo',
  otherDeviceBody:
    'Esta cuenta se está utilizando en otro dispositivo. Para continuar aquí, se cerrará la sesión del otro dispositivo.',
  otherDeviceConfirm: 'Continuar aquí',
  otherDeviceCancel: 'Cancelar',
  // STAFF: JUST SIGNED OUT (the login screen must not sign them straight back in)
  staffSignedOutTitle: 'Ha cerrado la sesión',
  staffSignedOutBody:
    'Su sesión en este dispositivo está cerrada. Inicie sesión de nuevo para recibir llamadas.',

  // ---------- STAFF: DASHBOARD (StaffDashboard) ----------
  staffAvailable: 'Disponible para llamadas',
  staffReconnecting: 'Reconectando…',
  staffNotReachable: 'Avisos de llamada desactivados',
  staffNotReachableHint: 'Mientras los avisos estén desactivados, las llamadas no le llegarán en este dispositivo.',
  staffAvailableHint: 'Mantenga esta pantalla abierta. Le avisaremos cuando reciba una llamada.',
  staffReconnectingHint: 'Estamos restableciendo la conexión. No cierre esta pantalla, por favor.',
  staffNotificationsHelp:
    'Para recibir avisos de llamada mientras esta pantalla está en segundo plano, active las notificaciones.',
  staffEnableNotifications: 'Activar avisos',
  staffNotificationsBlocked:
    'Este navegador está bloqueando las notificaciones de este sitio, así que las llamadas no pueden sonar en este dispositivo. Permita las notificaciones desde el icono del candado de la barra de direcciones y vuelva a pulsar el botón. Las ventanas privadas y de invitado no pueden recibir avisos de llamada: utilice una ventana normal.',
  staffNotificationsUnsupported:
    'Este navegador no puede recibir avisos de llamada. Utilice Chrome, Edge o Firefox actualizados en una ventana normal, o instale la aplicación.',
  staffNotificationsDismissed:
    'Se cerró la pregunta sin responder. Vuelva a pulsar el botón y elija Permitir.',
  staffNotificationsFailed:
    'Las notificaciones están permitidas, pero no se ha podido registrar este dispositivo para los avisos de llamada. Compruebe la conexión e inténtelo de nuevo.',
  staffRetryNotifications:
    'Volver a intentarlo',
  staffNotificationsEnabled:
    'Los avisos de llamada están activados en este dispositivo.',
  staffLogout: 'Cerrar sesión',
  // Shown when a notification is acted on after its call is already over.
  staffCallNoLongerActive: 'Esa llamada ya no está activa.',

  // ---------- STAFF: INSTALL APPLICATION (StaffDashboard, roadmap #100) ----------
  installAppButton: 'Instalar aplicación',
  installAppHint: 'Añada esta aplicación a su escritorio para recibir llamadas más rápido.',
  installFallbackTitle: 'Instalación no disponible',
  installFallbackBody:
    'Este navegador no permite instalar la aplicación. Para instalarla en su escritorio, abra esta página con Microsoft Edge o Google Chrome.',
  installFallbackClose: 'Cerrar',

  // ---------- STAFF: DESKTOP SETUP (StaffDashboard, roadmap #100 second follow-up) ----------
  desktopSetupHeading: 'Configuración de escritorio',
  desktopSetupInstallLabel: 'Instalar aplicación',
  desktopSetupInstallHint:
    'No se descarga ningún archivo: su navegador le pedirá confirmación y la aplicación se abrirá como una ventana propia.',
  desktopSetupInstalled: '✓ Aplicación instalada',
  desktopSetupStep2Label: 'Activar inicio automático',
  desktopSetupStep2Hint:
    'Descargue el archivo y haga doble clic sobre él: la ventana se abrirá sola al iniciar sesión y las llamadas seguirán sonando aunque la cierre.',
  desktopSetupDownload: 'Descargar configuración',
  desktopSetupDownloaded: 'Archivo descargado: haga doble clic sobre él y acepte el aviso del sistema.',
  desktopSetupConfirmRun: 'Ya lo he ejecutado',
  desktopSetupDownloadAgain: 'Volver a descargar',
  desktopSetupConfirmed:
    '✓ Inicio automático activado; se comprobará automáticamente en su próximo inicio de sesión.',
  desktopSetupVerified: '✓ Inicio automático activado y comprobado.',

  // ---------- STAFF: IN-CALL (StaffCallView) ----------
  staffDefaultCaller: 'Llamante',
  staffConnectingCall: 'Conectando la llamada…',
  staffCustomerMicOff: 'Micrófono del llamante silenciado',

  // ---------- STAFF: INCOMING CALL (StaffIncomingCall) ----------
  staffNoActiveCall: 'No hay ninguna llamada activa.',
  staffNoIncomingCall: 'No hay ninguna llamada entrante en este momento.',
  staffIncomingTitle: 'Llamada entrante',
  staffReject: 'Rechazar',
  staffAccept: 'Aceptar',
  // Operating-system notification body when the softphone window is not in front.
  staffIncomingNotificationBody: (who: string) => `${who} le está llamando.`,
  staffAlreadyAnswered: 'Otro miembro del equipo ya ha atendido esta llamada.',

  // ---------- PUBLIC STATIC / LEGAL PAGES (rehosted from the legacy frontend) ----------
  privacyPolicyTitle: 'Política de privacidad',
  deleteAccountTitle: 'Cómo eliminar la cuenta',

  // CONTACT PAGE (Contact) → POST /users/contactMail
  contactTitle: 'Contacte con nosotros',
  contactIntro: 'Envíenos su consulta y le responderemos lo antes posible.',
  contactNameLabel: 'Nombre',
  contactMessageLabel: 'Mensaje',
  contactPrivacyConsent: 'He leído y acepto la',
  contactPrivacyRequired: 'Acepte la política de privacidad para continuar.',
  contactPromotionConsent: 'Deseo recibir información comercial y ofertas promocionales por correo electrónico.',
  contactSubmit: 'Enviar',
  contactSending: 'Espere, por favor…',
  contactSuccess: 'Gracias. Su mensaje se ha enviado correctamente.',
  contactError: 'No hemos podido enviar su mensaje. Inténtelo de nuevo.',

  // LANDING PAGE (Landing)
  landingHeroTitle: 'Escanee, conecte, videollamada',
  landingHeroSubtitle: 'Videollamadas instantáneas con solo escanear un código QR.',
  landingCard1Title: 'Para uso personal',
  landingCard1Body:
    'Comparta un código QR, no su número de teléfono. Puede activar y desactivar sus códigos QR en cualquier momento.',
  landingCard2Title: 'Para empresas',
  landingCard2Body: 'Invite a su personal y cree horarios. Aporte un toque humano a sus clientes.',
  landingCard3Title: 'Sin descargas',
  landingCard3Body:
    'Quien llama no necesita ninguna aplicación. Al escanear el código QR se abre una página web con la llamada.',

  // ---------- CALLER: USER-VISIBLE CALL-STATUS TEXTS (core/VideoState) ----------
  statusConnecting: 'Estamos conectando su llamada. Espere, por favor…',
  statusCalling: 'Llamando…',
  statusWillAssistShortly: 'Le atenderemos en un momento…',
  statusEveryoneBusy:
    'Todos los miembros de nuestro equipo están atendiendo otras llamadas. No cierre esta pantalla; le atenderemos lo antes posible.',
  cameraActivationFailed:
    'No hemos podido encender la cámara. Compruebe su conexión y los permisos de la cámara.',
}
